import React, { useEffect, useRef, useState } from "react";
import "./Places.scss";
import { Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocalization } from '../../../contexts/LocalizationContext';
import AddressAutocomplete from "./AddressAutocomplete";


interface AddressFormState {
  pincode: string;
  address: string;
  line2: string;
  cityValue: string;
  stateValue?: string;
  county?: string;
  countryName: string;
  countryValue: string;
}

export interface iAddessProps {
  isDownload: boolean;
  isVerify: boolean;
  isThanks: boolean;
  validationErrors: Record<string, string>;
  isBillingReadonly: boolean;
  billingAddr: any;
  onPlaceUpdate: (
    data: AddressFormState,
  ) => void; // Callback for sending data to parent
  onFieldBlur?: (fieldName: string, value: string) => void;
}
const PlacesJapan: React.FC<iAddessProps> = ({
  isThanks,
  isDownload,
  isVerify,
  validationErrors,
  isBillingReadonly,
  billingAddr,
  onPlaceUpdate,
  onFieldBlur,
}) => {
  const isReadonly = isBillingReadonly;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const { locale, stateDropdown, countryList, countyField, states } = useLocalization();

  const [selectedCountryCode, setSelectedCountryCode] = useState(countryList[0].ISO_CC);
  const [addressState, setAddressState] = useState<AddressFormState>({
    pincode: "",
    address: "",
    line2: "",
    cityValue: "",
    ...(stateDropdown && { stateValue: "" }),
    ...(countyField && { county: "" }),
    countryName: countryList[0].countryName,
    countryValue: countryList[0].ISO_CC,
  });

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute("autoComplete", "none");
    }
  });

  const handlePlaceSelect = (place: any) => {
    const addressComponents = place.address_components;

    let newPincode = addressState.pincode || "";
    let newAddress = "";
    let geoStateValue = addressState.stateValue || "";
    let geoCityValue = addressState.cityValue || "";
    let geoCountyValue = addressState.county || "";
    let geoCountryValue = addressState.countryValue;
    let geoCountryName = addressState.countryName;

    if (addressComponents) {
      // Handle Google Places autocomplete selection
      let streetNumber = "";
      let route = "";
      for (const component of addressComponents) {
        if (component.types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (component.types.includes("route")) {
          route = component.long_name;
        }
        if (selectedCountryCode.toLowerCase() !== 'us' && component.types.includes("postal_town")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("locality")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          // For US addresses, use the state code
          if (stateDropdown) {
            const stateCode = component.short_name;
            // Verify if the state code exists in geoStates
            const validState = states?.find(
              (state) =>
                (state.en ? state.en === stateCode : state.code === stateCode) ||
                state.name === component.long_name
            );
            if (validState) {
              geoStateValue = stateCode
            }
          }
        }
        if (selectedCountryCode.toLowerCase() !== 'us' && (component.types.includes("administrative_area_level_2"))) {
          geoCountyValue = component.long_name;
        }
        if (component.types.includes("country")) {
          const countryCode = component.short_name.toUpperCase();
          // Find the country in the countryList
          const validCountry = countryList.find(country => country.ISO_CC === countryCode);
          if (validCountry) {
            geoCountryValue = countryCode;
            setSelectedCountryCode(countryCode);
            geoCountryName = validCountry?.countryName || "";
          }
        }
        if (component.types.includes("postal_code")) {
          newPincode = component.long_name;
        }
      }
      if (streetNumber || route) {
        newAddress = [streetNumber, route].filter(Boolean).join(" ");
      }
      if (!newAddress) {
        if (locale === 'ja-jp') {
          const parts = place.formatted_address.split(",");
          if (parts[0].indexOf("Japan") > -1) {
            newAddress = parts[parts.length - 2] + parts[parts.length - 1];
          } else {
            newAddress = parts[0] + parts[1];
          }
        } else if (locale !== 'es-es' && locale !== 'it-it' && locale !== 'en-gb') {
          newAddress = place.formatted_address.split(",")[0] || "";
        } else {
          newAddress = place.formatted_address.split(",")[0] + place.formatted_address.split(",")[1];
        }
      }
    } else if (place.target) {
      // Handle form field changes
      const { name, value } = place.target;
      newPincode = addressState.pincode || "";
      newAddress = addressState.address || "";
      geoCityValue = addressState.cityValue || "";
      geoStateValue = addressState.stateValue || "";
      geoCountyValue = addressState.county || "";
      geoCountryValue = selectedCountryCode || "";
      geoCountryName = countryList.find(country => country.ISO_CC === selectedCountryCode)?.countryName || "";

      if (name === `countryValueBilling`) {
        // Handle country dropdown change
        const selectedCountry = countryList.find(country => country.ISO_CC === value);
        if (selectedCountry) {
          geoCountryValue = selectedCountry.ISO_CC;
          geoCountryName = selectedCountry.countryName;

          // Update selected country code for Places API
          setSelectedCountryCode(value);

          // Reset Google Places input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        } else {
          newPincode = "";
          newAddress = "";
          geoCityValue = "";
          geoStateValue = "";
          geoCountyValue = "";
        }
      } else if (name === `addressBilling`) {
        newAddress = value || "";
      } else if (name === `cityBilling`) {
        geoCityValue = value;
      } else if (name === `stateNameBilling`) {
        geoStateValue = value;
      } else if (name === `countyBilling`) {
        geoCountyValue = value;
      } else if (name === `zipBilling`) {
        newPincode = value;
      }
    }

    // Update address state
    const newAddressState = {
      ...addressState,
      pincode: newPincode,
      address: newAddress,
      cityValue: geoCityValue,
      ...(stateDropdown && { stateValue: geoStateValue }),
      ...(countyField && { county: geoCountyValue }),
      countryName: geoCountryName,
      countryValue: geoCountryValue,
    };

    setAddressState(newAddressState);
  };

  const handlePlaceBlur = (event: React.FocusEvent<HTMLElement>) => {
    handlePlaceSelect(event);
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const fieldMap: Record<string, string> = {
      addressBilling: "address",
      line2Billing: "line2",
      cityBilling: "cityValue",
      stateNameBilling: "stateValue",
      zipBilling: "pincode",
      countryValueBilling: "countryValue",
    };
    const fieldName = fieldMap[target.name];
    if (fieldName) {
      onFieldBlur?.(fieldName, target.value);
    }
  };

  useEffect(() => {
    let addrData: any = {
      ...{ pincode: addressState.pincode },
      ...{ address: addressState.address },
      ...{ line2: addressState.line2 },
      ...{ cityValue: addressState.cityValue },
      ...(stateDropdown && { stateValue: addressState.stateValue }),
      ...(countyField && { county: addressState.county }),
      ...{ countryName: addressState.countryName },
      ...{ countryValue: addressState.countryValue },
    }

    if (!addressState.pincode && !addressState.address && !addressState.cityValue && !addressState.stateValue) {
      addrData = {}
    };
    if (Object.keys(addrData).length > 0) {
      onPlaceUpdate(addrData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);
  useEffect(() => {
    if (billingAddr) {
      const {
        postalCode,
        line1,
        city,
        countrySubdivision,
        county,
        countryName,
        country,
        line2,
      } = JSON.parse(billingAddr);
      setAddressState({
        ...addressState,
        pincode: postalCode,
        address: line1,
        line2: line2,
        cityValue: city,
        stateValue: countrySubdivision || '',
        county: county || '',
        countryName: countryName,
        countryValue: country,
      });

      onPlaceUpdate(
        {
          pincode: postalCode,
          address: line1,
          line2: line2,
          cityValue: city,
          stateValue: countrySubdivision || '',
          county: county || '',
          countryName: countryName,
          countryValue: country,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddr]);
  return (
    <div className={`Places Billing`}>
      <Col md={6}>
        <Form.Group controlId={`zipBilling`}>
          <Form.Label className="visually-hidden" htmlFor={`zipBilling`}>{t("ZIP_CODE_COLON")}</Form.Label>
          <Form.Control
            id={`zipBilling`}
            placeholder={t("ZIP_CODE_REQUIRED")}
            type="text"
            autoComplete="none"
            readOnly={isReadonly}
            className={`form-control  ${isReadonly ? "disabled" : ""} `}
            name={`zipBilling`}
            value={addressState.pincode}
            isInvalid={!!validationErrors[`billingpincode`] || !!validationErrors[`pincode`]}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            aria-required="true"
            aria-invalid={!!validationErrors[`billingpincode`] || !!validationErrors[`pincode`]}
            aria-describedby={(validationErrors[`billingpincode`] || validationErrors[`pincode`]) ? `zipBillingError` : undefined}
          />
          {(validationErrors[`billingpincode`] || validationErrors[`pincode`]) && (
            <Form.Control.Feedback type="invalid" id={`zipBillingError`} aria-live="polite">
              {validationErrors[`billingpincode`] || validationErrors[`pincode`]}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>
      {stateDropdown && (
        <Col md={6}>
          <Form.Group controlId={`stateNameBilling`}>
            <Form.Label className="visually-hidden" htmlFor={`stateNameBilling`}>{t("STATE")}</Form.Label>
            <Form.Select
              id={`stateNameBilling`}
              autoComplete="none"
              value={addressState.stateValue}
              disabled={isReadonly}
              className={`form-control  ${isReadonly ? "disabled" : ""} `}
              name={`stateNameBilling`}
              onChange={handlePlaceSelect}
              onBlur={handlePlaceBlur}
              isInvalid={!!validationErrors[`stateValue`]}
              aria-required="true"
              aria-invalid={!!validationErrors[`stateValue`]}
              aria-describedby={validationErrors[`stateValue`] ? `stateNameBillingError` : undefined}
            >
              <option value="">{t("SELECT_STATE")}</option>
              {states?.map((geoState, index) => (
                <option key={index} value={geoState.code}>
                  {geoState.name}
                </option>
              ))}
            </Form.Select>
            {validationErrors[`stateValue`] && (
              <Form.Control.Feedback type="invalid" id={`stateNameBillingError`} aria-live="polite">
                {validationErrors[`stateValue`]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>)}
      <Col md={6}><Form.Group controlId={`cityBilling`}>
        <Form.Label className="visually-hidden" htmlFor={`cityBilling`}>{t("CITY_COLON")}</Form.Label>
        <Form.Control
          id={`cityBilling`}
          placeholder={t("CITY_REQUIRED")}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`cityBilling`}
          value={addressState.cityValue}
          isInvalid={!!validationErrors[`cityValue`]}
          onChange={handlePlaceSelect}
          onBlur={handlePlaceBlur}
          aria-required="true"
          aria-invalid={!!validationErrors[`cityValue`]}
          aria-describedby={validationErrors[`cityValue`] ? `cityBillingError` : undefined}
        />
        {validationErrors[`cityValue`] && (
          <Form.Control.Feedback type="invalid" id={`cityBillingError`} aria-live="polite">
            {validationErrors[`cityValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col><Col md={6}><Form.Group controlId={`addressBilling`}>
        <Form.Label className="visually-hidden" htmlFor={`addressBilling`}>{t("ADDRESS1_COLON")}</Form.Label>
        {isDownload ? (
          <Form.Control
            id={`addressBilling`}
            placeholder={t("ADDRESS1_REQUIRED")}
            className={`form-control ${validationErrors[`address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            type="text"
            autoComplete="none"
            name={`addressBilling`}
            readOnly={isReadonly}
            value={addressState.address}
            isInvalid={!!validationErrors[`address`]}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            aria-required="true"
            aria-invalid={!!validationErrors[`address`]}
            aria-describedby={validationErrors[`address`] ? `addressBillingError` : undefined}
          />
        ) : (
          <AddressAutocomplete
            id={`addressBilling`}
            name={`addressBilling`}
            className={`form-control ${validationErrors[`address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            inputRef={inputRef}
            placeholder={t("ADDRESS1_REQUIRED")}
            countryCode={selectedCountryCode}
            onPlaceSelected={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            onChange={handlePlaceSelect}
            value={addressState.address}
            readOnly={isReadonly}
            disabled={isReadonly}
            isInvalid={!!validationErrors[`address`]}
            ariaRequired
            ariaDescribedBy={validationErrors[`address`] ? `addressBillingError` : undefined}
          />
        )}
        {validationErrors[`address`] && (
          <Form.Control.Feedback type="invalid" id={`addressBillingError`} aria-live="polite">
            {validationErrors[`address`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
      <Col md={6}><Form.Group controlId={`line2Billing`}>
        <Form.Label className="visually-hidden" htmlFor={`line2Billing`}>{t("LINE2_OPTIONAL_COLON")}</Form.Label>
        <Form.Control
          id={`line2Billing`}
          placeholder={t("LINE2_OPTIONAL")}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`line2Billing`}
          value={addressState.line2}
          onChange={(event: any) => {
            setAddressState({
              ...addressState,
              line2: event.target.value,
            })
          }}
        />
      </Form.Group>
      </Col>
      <Col md={6}><Form.Group controlId={`countryValueBilling`}
      >
        <Form.Label className="visually-hidden" htmlFor={`countryValueBilling`}>{t("COUNTRY_REQUIRED")}</Form.Label>
        <Form.Select
          id={`countryValueBilling`}
          autoComplete="none"
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`countryValueBilling`}
          disabled={isReadonly}
          value={addressState.countryValue}
          onChange={handlePlaceSelect}
          onBlur={handlePlaceBlur}
          isInvalid={!!validationErrors[`countryValue`]}
          aria-required="true"
          aria-invalid={!!validationErrors[`countryValue`]}
          aria-describedby={validationErrors[`countryValue`] ? `countryValueBillingError` : undefined}
        >
          {countryList.map((country) => (
            <option key={country.ISO_CC} value={country.ISO_CC}>
              {country.countryName}
            </option>
          ))}
        </Form.Select>
        {validationErrors[`countryValue`] && (
          <Form.Control.Feedback type="invalid" id={`countryValueBillingError`} aria-live="polite">
            {validationErrors[`countryValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
    </div>
  );
};
export default PlacesJapan;